import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxRBACService } from "app/bfx-common/services/bfx-rbac.service";

import {
  BenchmarkDataPrimaryLookupService,
  BfxBlotterAction,
  BfxBlotterContextMenuItem,
  Roles,
  ActiveUserProfilePromiseService,
  ActiveUser,
  SysFeatures,
  BenchmarkPositionLookupService,
  SpotRiskModel,
  BmarkFixingType,
  CubiqWorker,
} from "shared/cubiq-services";

import { SystemConfigPromiseService } from "shared/auth-services";

import {
  BfxBaseBlotterContextMenuService,
  BfxBlotterActionSubject,
} from "../../../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BmarkSpotRiskDataService } from "./bmark-spot-risk-data.service";
import { BmarkClientOrdersBlotterMode } from "shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { LoggerService } from "@bfx/logger";
import { BmarkSpotRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import { DialogService } from "@bfx/primeng/dynamicdialog";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { Subject, Subscription } from "rxjs";
import { EXPORT_INPUT_TYPE, EXPORT_OUTPUT_TYPE } from "app/bfx-dialogs/bfx-dialog-export.component";
const logger = LoggerService.for(__dirname);

export class BmarkSpotRiskContextMenuItem extends BfxBlotterContextMenuItem<any> {
  constructor(
    public actionId: BfxBlotterAction,
    title: string = "",
    public iconClass: string = " ",
    public iconClassRight?: string,
  ) {
    super(
      actionId,
      title,
      () => {
        return;
      },
      () => {
        return;
      },
    );
    this.state.available = true;
  }
}

@Injectable()
export class BmarkSpotRiskContextMenuService extends BfxBaseBlotterContextMenuService<any> {
  public activeMenuItems: Array<BmarkSpotRiskContextMenuItem>;
  public recentMenuItems: Array<BmarkSpotRiskContextMenuItem>;

  public selectedBenchmarksClientOrders: Array<CubiqTS.OrderDataImpl> = [];
  protected permissions = {
    canRouteRisk: null,
    canRouteDealt: null,
  };
  protected activeUser: ActiveUser;
  protected manualRiskRouting: boolean = false;
  protected autoHedgeEnabled: boolean = false;
  protected canMarkAsManual: boolean = false;

  private spotRiskDialogSubject = new Subject<BfxDialogData>();
  public dialogCtrl$ = this.spotRiskDialogSubject.asObservable();

  private autoHedgeSub: Subscription;
  private autoHedgingEnabled: boolean = false;

  constructor(
    private dataPromiseLookupService: BenchmarkDataPrimaryLookupService,
    private rbacService: BfxRBACService,
    private activeUserService: ActiveUserProfilePromiseService,
    private bmarkSpotRiskDataService: BmarkSpotRiskDataService,
    private systemConfigService: SystemConfigPromiseService, // @Inject(LOCALE_ID) localeId: string,
    private positionService: BenchmarkPositionLookupService,
    private spotRiskDataService: BmarkSpotRiskDataService,
    protected dialogService: DialogService,
    protected cubiqWorker: CubiqWorker,
  ) {
    super(new BfxBlotterActionSubject());
    this.subscribeAutoHedgeCircuitBreaker();
    this.initializePermissions();
  }

  LOG_PREFIX: string = "[BmarkSpotRiskContextMenuService] ";
  TRACE_LOGGING: boolean = true;

  private traceLog(logString: string) {
    if (this.TRACE_LOGGING) logger.out.info(this.LOG_PREFIX + logString);
  }

  private initializePermissions() {
    // dynamic permissions
    Promise.all([
      this.activeUserService.getActiveUserData(),
      this.rbacService.hasRole(Roles.ROLE_ORDER_BENCHMARK_MANUAL_RISK_ROUTE),
      this.rbacService.hasRole(Roles.ROLE_ORDER_BENCHMARK_MANUAL_RISK_ROUTE_FROM_DEALT),
      this.systemConfigService.getSystemConfig(SysFeatures.manualRiskRouting),
      this.rbacService.hasRole(Roles.ROLE_BM_AUTO_HEDGING_POSITION_CONTROL),
      this.systemConfigService.getSystemConfig(SysFeatures.bmAutoHedging),
    ]).then(
      ([
        activeUser,
        canRouteRisk,
        canRouteDealt,
        manualRiskRoutingEnabled,
        canSetManual,
        autoHedgeEnabled,
      ]) => {
        this.activeUser = activeUser;
        this.permissions.canRouteRisk = canRouteRisk;
        this.permissions.canRouteDealt = canRouteDealt && canRouteRisk;
        this.manualRiskRouting =
          manualRiskRoutingEnabled != null && manualRiskRoutingEnabled.toUpperCase() == "Y";
        this.canMarkAsManual = canSetManual;
        this.autoHedgeEnabled = autoHedgeEnabled != null && autoHedgeEnabled.toUpperCase() == "Y";
        this.initMenuItems();
        this.actionSubject.subscribe((event) => {
          switch (event.action) {
            case BfxBlotterAction.ACTION_SPOT_RISK_DISABLE_AUTO: {
              this.changeAutoHedgeForItems(event.selectedItems, CubiqTS.HedgingModeType.MANUAL);
              break;
            }
            case BfxBlotterAction.ACTION_SPOT_RISK_ENABLE_AUTO: {
              this.changeAutoHedgeForItems(event.selectedItems, CubiqTS.HedgingModeType.AUTO);
              break;
            }
            case BfxBlotterAction.ACTION_SPOT_RISK_EXPORT_TO_CSV: {
              this.exportToCSV(event.selectedItems);
              break;
            }
          }
        });
      },
    );
  }

  protected getEnabledActions(
    items: any[],
    selectedOrderIds: Array<string>,
  ): Array<BfxBlotterAction> {
    let enabledItems = [];

    let fixingType: BmarkFixingType = this.bmarkSpotRiskDataService.getActiveFixingType();

    if (BmarkFixingType.ACTIVE == fixingType && this.manualRiskRouting) {
      enabledItems.push(BfxBlotterAction.ACTION_SPOT_RISK_SEND_TO);
    }

    enabledItems.push(BfxBlotterAction.ACTION_SPOT_RISK_EXPORT_TO_CSV);

    if (this.autoHedgingEnabled && BmarkFixingType.ACTIVE == fixingType) {
      if (items != null && items.length > 0) {
        let thereIsNoManual: boolean = items.some(
          (i) => this.positionService.getHedgingMode(i.id) != CubiqTS.HedgingModeType.MANUAL,
        );
        let isRisk =
          this.bmarkSpotRiskDataService.getBlotterMode() === BmarkClientOrdersBlotterMode.RISK;
        if (isRisk && thereIsNoManual) {
          enabledItems.push(BfxBlotterAction.ACTION_SPOT_RISK_DISABLE_AUTO);
        }

        let thereIsManual: boolean = items.some(
          (i) =>
            this.positionService.getHedgingMode(i.id) === CubiqTS.HedgingModeType.MANUAL &&
            this.positionService.getHedgingModeChangeUserId(i.id) instanceof CubiqTS.Long &&
            this.positionService.getHedgingModeChangeUserId(i.id).toNumber() > 0,
        );

        if (isRisk && thereIsManual) {
          enabledItems.push(BfxBlotterAction.ACTION_SPOT_RISK_ENABLE_AUTO);
        }
      }
    }

    return enabledItems;
  }

  protected getActionDecorators(items: Array<any>): any {
    return [];
  }

  protected prepareActions(ladderBlotter: any): void {}

  public getMenuItems(): Array<BmarkSpotRiskContextMenuItem> {
    let ret: Array<BmarkSpotRiskContextMenuItem> = [];

    let fixingType: BmarkFixingType = this.bmarkSpotRiskDataService.getActiveFixingType();
    switch (fixingType) {
      case BmarkFixingType.ACTIVE: {
        // Choose which of the
        let dealtItems = [...this.activeMenuItems];
        const riskMode =
          this.bmarkSpotRiskDataService.getBlotterMode() == BmarkClientOrdersBlotterMode.RISK;
        if (riskMode) {
          // In Risk mode remove the NOP items and leave the active operations
          dealtItems = dealtItems.filter(
            (menuItem) => menuItem.actionId !== BfxBlotterAction.ACTION_NOP,
          );
        } else {
          // In Dealt mode, remove the 2 actual autohedge items operations, leaving just the NOP items
          dealtItems = dealtItems.filter(
            (menuItem) =>
              menuItem.actionId !== BfxBlotterAction.ACTION_SPOT_RISK_DISABLE_AUTO &&
              menuItem.actionId !== BfxBlotterAction.ACTION_SPOT_RISK_ENABLE_AUTO,
          );
        }
        ret = this.reduceVisibleMenuItems(dealtItems);
        break;
      }
      case BmarkFixingType.RECENT: {
        ret = this.recentMenuItems;
        break;
      }
    }

    return ret;
  }

  public buildMenu(
    blotter: import("../../../../bfx-base-blotter/bfx-base-blotter").BfxBaseBlotter,
    pageX: number,
    pageY: number,
    selectedIds: string[],
    selectedItems?: object[],
  ): void {
    let hasPermissions: boolean =
      (this.bmarkSpotRiskDataService.getBlotterMode() == BmarkClientOrdersBlotterMode.DEALT &&
        this.permissions.canRouteDealt) ||
      (this.bmarkSpotRiskDataService.getBlotterMode() == BmarkClientOrdersBlotterMode.RISK &&
        this.permissions.canRouteRisk);

    if (
      // this.manualRiskRouting &&
      selectedItems &&
      hasPermissions &&
      this.activeUser.userTypeId == CubiqTS.UserType.TRADER
    ) {
      let enabledItems: Array<BfxBlotterAction> = this.getEnabledActions(
        selectedItems,
        selectedIds,
      );
      let actionDecorators: any = this.getActionDecorators(selectedItems);
      this.showMenuAt(blotter, pageX, pageY, enabledItems, actionDecorators);
    }
  }

  protected initMenuItems(): void {
    this.activeMenuItems = [];
    this.recentMenuItems = [];

    this.activeMenuItems.push(
      new BmarkSpotRiskContextMenuItem(
        BfxBlotterAction.ACTION_SPOT_RISK_SEND_TO,
        "Send To ...",
        " ",
        "fa icon-ion_triangle_right_filled order__menu__menuItem__icon__vwap_right",
      ),
    );

    if (this.autoHedgeEnabled === true && this.canMarkAsManual === true) {
      this.activeMenuItems.push(new BmarkSpotRiskContextMenuItem(BfxBlotterAction.SEPARATOR));
      let disableAutoItem = new BmarkSpotRiskContextMenuItem(
        BfxBlotterAction.ACTION_SPOT_RISK_DISABLE_AUTO,
        "Disable Auto Hedging",
        " ",
        "",
      );
      let enableAutoItem = new BmarkSpotRiskContextMenuItem(
        BfxBlotterAction.ACTION_SPOT_RISK_ENABLE_AUTO,
        "Enable Auto Hedging",
        " ",
        "",
      );
      this.activeMenuItems.push(disableAutoItem);
      this.activeMenuItems.push(enableAutoItem);
      disableAutoItem = new BmarkSpotRiskContextMenuItem(
        BfxBlotterAction.ACTION_NOP,
        "Disable Auto Hedging (Risk View)",
        " ",
        "",
      );
      enableAutoItem = new BmarkSpotRiskContextMenuItem(
        BfxBlotterAction.ACTION_NOP,
        "Enable Auto Hedging (Risk View)",
        " ",
        "",
      );
      this.activeMenuItems.push(disableAutoItem);
      this.activeMenuItems.push(enableAutoItem);
    }

    let exportMenuItem: BmarkSpotRiskContextMenuItem = new BmarkSpotRiskContextMenuItem(
      BfxBlotterAction.ACTION_SPOT_RISK_EXPORT_TO_CSV,
      "Export to CSV",
      " ",
      "",
    );

    this.activeMenuItems.push(
      new BmarkSpotRiskContextMenuItem(BfxBlotterAction.SEPARATOR),
      exportMenuItem,
    );

    this.recentMenuItems.push(exportMenuItem);
  }

  protected changeAutoHedgeForItems(
    items: Array<BmarkSpotRiskDisplayModel>,
    hedgeMode: CubiqTS.HedgingModeType,
  ): void {
    let pairs: Set<string> = new Set();

    logger.out.info(`${this.constructor.name}: Changing hedge mode for ${items.length} requested.`);

    items = items.filter((i) => this.positionService.getHedgingMode(i.id) != hedgeMode);

    logger.out.info(`${this.constructor.name}: Changing hedge mode for ${items.length} positions.`);

    items.forEach((i) => pairs.add(i.ccyPair));

    let fixingData = this.spotRiskDataService.getSelectedFixingData();
    if (fixingData != null) {
      let bmTimeCode = new CubiqTS.BMTimeCodeImpl();
      bmTimeCode.code = fixingData.code;
      bmTimeCode.time = CubiqTS.moment(fixingData.fixingTimestamp);
      this.positionService.changeAutoHedgeForPositions(bmTimeCode, [...pairs], hedgeMode);
    } else {
      logger.out.error(
        `${this.constructor.name}: Could not find active fixing data when ` +
          `running enable/disable auto hedge for spot risk position.`,
      );
    }
  }

  protected exportToCSV(selectedItems: Array<BmarkSpotRiskDisplayModel>) {
    logger.out.info(`${this.constructor.name}: Exporting ${selectedItems.length} pairs to CSV.`);
    let spotRiskModel: SpotRiskModel = this.spotRiskDataService.spotRiskModel;

    let rows: string[][] = [];
    if (spotRiskModel.spotRiskData && spotRiskModel.spotRiskData.size > 0) {
      for (const pairData of selectedItems) {
        let pair: string = pairData.ccyPair;
        let ccy: string = pairData.baseCcy;
        let op: string = pairData.totalInternal.gte(0) ? "BUY" : "SELL";
        let amt: CubiqTS.BigNumber = pairData.totalInternal.abs();

        let row: string[] = [pair, op, ccy, amt.toString()];
        rows.push(row);
      }
    }
    logger.out.info(`${this.constructor.name}: ... Exported`);

    let headers: string[] = ["CcyPair", 'Dir"', "Ccy", "Total (Internal)"];
    logger.out.info(`${headers}`);
    rows.forEach((row) => {
      logger.out.info(`${row}`);
    });

    this.doExportDialog(headers, rows);
  }

  doExportDialog(headers: string[], rows: string[][]) {
    let payload = {
      title_text: "Export Benchmark Spot Risk as CSV",
      message_text: "Do you want to export data for all selected positions?",
      headers: headers,
      rows: rows,
      export_input_type: EXPORT_INPUT_TYPE.ARRAY_DATA,
      export_output_type: EXPORT_OUTPUT_TYPE.CSV_EXPORT,
      blotterName: "benchmark_spot_risk",
    };

    this.spotRiskDialogSubject.next(
      new BfxDialogData(BfxDialogCommand.DIALOG_CSV_EXPORT_OPEN, null, null, payload),
    );
  }

  subscribeAutoHedgeCircuitBreaker() {
    // prime it
    this.cubiqWorker
      .getBroker()
      .primeStaticData<CubiqTS.StaticDataProfileImpl>(CubiqTS.StaticDataProfileImpl.name)
      .subscribe((profile: CubiqTS.StaticDataProfileImpl) => {
        logger.out.info(`HedgingStatusChange Prime result`);
        this.autoHedgingEnabled = profile.autoHedgingStatus.autoHedgingEnabled;
      });

    // subscribe
    this.autoHedgeSub = this.dataPromiseLookupService.autoHedgeSubject.subscribe((response) => {
      logger.out.info(`HedgingStatusChange Update ${response.status.autoHedgingEnabled}`);
      this.autoHedgingEnabled = response.status.autoHedgingEnabled;
    });
  }
}
